import type { PieceModel } from '@doer/frontend-components/creativity-editor';

/**
 * Utilidades para hidratar / dehidratar las URLs de scene dentro de un PieceModel.
 *
 * Contexto: el piece guardado en S3 embebe URLs literales de scenes (videos y
 * audios TTS). Después de una edición desde Studio, el contenido físico en S3
 * cambia pero el path es estable (deriva del sceneId). Sin refresco, el editor
 * puede terminar sirviendo la versión cacheada vieja.
 *
 * Estrategia:
 *   - Al cargar el piece de S3 → walkeamos layers y reemplazamos las URLs de
 *     scene con las URLs frescas del backend (que ya vienen con `?v=<hash>`).
 *   - Antes de guardar → strippeamos el query string de las URLs de scene, así
 *     el piece en S3 queda con URLs canónicas y no cristaliza hashes viejos.
 *
 */

/**
 * Matchea URLs de scene assets. Captura kind (`videos` | `tts`) y sceneId (UUID v4).
 * No matchea placas (`/placas/...`), música (`/audios/...`), ni assets del user.
 */
const SCENE_URL_PATTERN =
  /\/(videos|tts)\/scene-([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\.(mp4|wav)/;

export type SceneAssetKind = 'video' | 'audio';
export type SceneAssetRef = { sceneId: string; kind: SceneAssetKind };

/** Layer del PieceModel visto como bag opcional de `content.src` string. */
type LayerWithSrc = { content?: { src?: unknown } };

/** Extrae `content.src` si es string; devuelve `undefined` para layers sin src. */
const getLayerSrc = (layer: unknown): string | undefined => {
  const src = (layer as LayerWithSrc).content?.src;
  return typeof src === 'string' ? src : undefined;
};

/** Setea `content.src` en un layer. El layer se muta in-place (típicamente sobre un clone). */
const setLayerSrc = (layer: unknown, src: string): void => {
  (layer as { content: { src: string } }).content.src = src;
};

export const parseSceneRefFromUrl = (url: string): SceneAssetRef | null => {
  const match = url.match(SCENE_URL_PATTERN);
  if (!match) return null;
  return { sceneId: match[2], kind: match[1] === 'videos' ? 'video' : 'audio' };
};

export const stripQueryString = (url: string): string => {
  const idx = url.indexOf('?');
  return idx === -1 ? url : url.slice(0, idx);
};

/**
 * Rewriter aplicado por `rewriteSceneLayerUrls` a cada URL de scene.
 * Devolver `undefined` deja el layer intacto (útil cuando el rewriter decide skipear
 * un caso - p.ej. scene borrada en hydrate).
 */
type SceneUrlRewriter = (currentSrc: string, ref: SceneAssetRef) => string | undefined;

/**
 * Walker interno: clona el piece y aplica `rewrite` a cada layer cuya `content.src`
 * matchea el pattern de scene. Layers no-scene (placa, música, uploads del user)
 * no se tocan. El return preserva el tipo exacto que entró (generic).
 */
const rewriteSceneLayerUrls = <T extends PieceModel>(piece: T, rewrite: SceneUrlRewriter): T => {
  const clone = structuredClone(piece);
  for (const layer of Object.values(clone.layers)) {
    const src = getLayerSrc(layer);
    if (!src) continue;
    const ref = parseSceneRefFromUrl(src);
    if (!ref) continue;
    const next = rewrite(src, ref);
    if (next !== undefined) setLayerSrc(layer, next);
  }
  return clone;
};

/**
 * Strippea el query string (`?v=<hash>`) de las URLs de scene antes de guardar el
 * piece en S3.
 */
export const dehydrateSceneUrls = <T extends PieceModel>(piece: T): T =>
  rewriteSceneLayerUrls(piece, src => stripQueryString(src));

/**
 * Reemplaza las URLs de scene en el piece con las vigentes indicadas por el map
 * `sceneId → { videoUrl, audioUrl }`.
 */
export const hydrateSceneUrls = <T extends PieceModel>(
  piece: T,
  freshBySceneId: Map<string, { videoUrl?: string | null; audioUrl?: string | null }>
): T =>
  rewriteSceneLayerUrls(piece, (_src, ref) => {
    const fresh = freshBySceneId.get(ref.sceneId);
    if (!fresh) return undefined;
    const next = ref.kind === 'video' ? fresh.videoUrl : fresh.audioUrl;
    return next ?? undefined;
  });
